import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { adminAPI } from '../api';

// ─── Helpers ───────────────────────────────────────────────────────────────────

const BASE_URL = (
  (typeof process !== 'undefined' && process.env?.REACT_APP_API_URL)
    ? process.env.REACT_APP_API_URL
    : (window.REACT_APP_API_URL || 'http://localhost:3001/api')
).replace('/api', '');

function parseDetails(details) {
  try { return typeof details === 'string' ? JSON.parse(details) : (details || {}); }
  catch { return {}; }
}

// ─── ReviewModal (must be outside Transactions to avoid remount crashes) ───────

function ReviewModal({ tx, note, setNote, processing, onClose, onProcess }) {
  const details = parseDetails(tx.payment_details);
  const isMoMo = tx.payment_method && tx.payment_method !== 'bank_transfer';
  const isWithdrawal = tx.type === 'withdrawal';
  const txId = tx.transaction_id || details.transaction_id;
  const senderName = tx.sender_name || details.sender_name;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()} style={{ maxWidth: 500 }}>
        <div className="modal-title">Review Transaction #{tx.id}</div>

        <div style={{ marginBottom: 14, fontSize: 14, lineHeight: 2 }}>
          <p><strong>👤 User:</strong> {tx.username} ({tx.email})</p>
          <p><strong>📋 Type:</strong> {tx.type.replace(/_/g, ' ')}</p>
          <p><strong>💵 Amount:</strong> GHS {parseFloat(tx.amount).toFixed(2)}</p>
          {tx.fee > 0 && <p><strong>💰 Fee:</strong> GHS {parseFloat(tx.fee).toFixed(2)}</p>}
          <p><strong>✅ Net Amount:</strong> GHS {parseFloat(tx.net_amount).toFixed(2)}</p>
          {tx.payment_method && (
            <p><strong>💳 Method:</strong> {tx.payment_method.replace(/_/g, ' ').toUpperCase()}</p>
          )}

          {isWithdrawal && (
            <p style={{ background: '#eff6ff', padding: '6px 10px', borderRadius: 6, border: '1px solid #93c5fd' }}>
              <strong>📱 Withdraw To:</strong>{' '}
              {details.account_number ? (
                <code style={{ fontWeight: 700, fontSize: 14, color: '#1e40af' }}>{details.account_number}</code>
              ) : (
                <span style={{ color: '#dc2626' }}>⚠️ Not provided</span>
              )}
              {details.account_name && <span> — {details.account_name}</span>}
              {details.bank_name && <span> ({details.bank_name})</span>}
            </p>
          )}

          {isMoMo && !isWithdrawal && (
            <>
              {txId ? (
                <p style={{ background: '#f0fff4', padding: '6px 10px', borderRadius: 6, border: '1px solid #86efac' }}>
                  <strong>🔑 Transaction ID:</strong>{' '}
                  <code style={{ fontWeight: 700, fontSize: 14, color: '#166534' }}>{txId}</code>
                </p>
              ) : (
                <p style={{ background: '#fff5f5', padding: '6px 10px', borderRadius: 6, border: '1px solid #fca5a5' }}>
                  <strong>🔑 Transaction ID:</strong>{' '}
                  <span style={{ color: '#dc2626' }}>⚠️ Not provided</span>
                </p>
              )}

              {senderName ? (
                <p style={{ background: '#f0fff4', padding: '6px 10px', borderRadius: 6, border: '1px solid #86efac' }}>
                  <strong>👤 Sender Name:</strong>{' '}
                  <span style={{ fontWeight: 700, color: '#166534' }}>{senderName}</span>
                </p>
              ) : (
                <p style={{ background: '#fff5f5', padding: '6px 10px', borderRadius: 6, border: '1px solid #fca5a5' }}>
                  <strong>👤 Sender Name:</strong>{' '}
                  <span style={{ color: '#dc2626' }}>⚠️ Not provided</span>
                </p>
              )}
            </>
          )}

          {!isWithdrawal && tx.payment_method === 'bank_transfer' && details.account_number && (
            <p><strong>🏦 Account:</strong> {details.account_number} ({details.account_name})</p>
          )}

          {tx.proof_image && (
            <p>
              <strong>📸 Proof:</strong>{' '}
              <a href={`${BASE_URL}${tx.proof_image}`} target="_blank" rel="noreferrer"
                style={{ color: 'var(--blue-mid)', fontWeight: 600 }}>
                View Screenshot ↗
              </a>
            </p>
          )}

          {tx.admin_note && (
            <p style={{ background: '#f8f9fa', padding: '6px 10px', borderRadius: 6, fontSize: 12 }}>
              <strong>📝 Previous Note:</strong> {tx.admin_note}
            </p>
          )}

          <p><strong>🔗 Reference:</strong> <code style={{ fontSize: 12 }}>{tx.reference}</code></p>
          <p><strong>📅 Date:</strong> {new Date(tx.created_at).toLocaleString()}</p>
        </div>

        {tx.status === 'completed' && tx.admin_note && tx.admin_note.includes('Auto-approved') && (
          <div style={{ background: '#f0fff4', border: '1px solid #86efac', borderRadius: 8, padding: 10, marginBottom: 14, fontSize: 13, color: '#166534' }}>
            ✅ This deposit was <strong>auto-approved</strong> by the SMS monitoring system.
          </div>
        )}

        <div className="form-group">
          <label className="form-label">Admin Note (Optional)</label>
          <input className="form-control" placeholder="e.g. Payment confirmed via MoMo"
            value={note} onChange={e => setNote(e.target.value)} />
        </div>

        <div className="modal-actions">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          {tx.status === 'pending' && (
            <>
              <button className="btn btn-danger" onClick={() => onProcess(tx, 'reject')}
                disabled={processing === tx.id}>❌ Reject</button>
              <button className="btn btn-success" onClick={() => onProcess(tx, 'approve')}
                disabled={processing === tx.id}>
                {processing === tx.id ? 'Processing...' : '✅ Approve'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────

export default function Transactions() {
  const [txs, setTxs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ type: 'deposit', status: 'pending' });
  const [processing, setProcessing] = useState(null);
  const [note, setNote] = useState('');
  const [showModal, setShowModal] = useState(null);

  const load = (f = filters) => {
    setLoading(true);
    adminAPI.transactions(f).then(r => setTxs(r.data.transactions || [])).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const applyFilters = (key, val) => {
    const f = { ...filters, [key]: val };
    setFilters(f);
    load(f);
  };

  const process = async (tx, action) => {
    setProcessing(tx.id);
    try {
      await adminAPI.processTransaction(tx.id, { action, admin_note: note });
      toast.success(`Transaction ${action}d!`);
      setShowModal(null);
      setNote('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    } finally {
      setProcessing(null);
    }
  };

  const typeIcon = {
    deposit: '💳', withdrawal: '💸', income: '📈',
    referral_bonus: '👥', checkin_bonus: '📅', registration_bonus: '🎁',
  };

  return (
    <div>
      {/* Filters */}
      <div className="filters">
        <select className="form-control" style={{ width: 160 }} value={filters.type} onChange={e => applyFilters('type', e.target.value)}>
          <option value="">All Types</option>
          <option value="deposit">Deposit</option>
          <option value="withdrawal">Withdrawal</option>
          <option value="income">Income</option>
          <option value="referral_bonus">Referral Bonus</option>
        </select>
        <select className="form-control" style={{ width: 160 }} value={filters.status} onChange={e => applyFilters('status', e.target.value)}>
          <option value="">All Status</option>
          <option value="pending">Pending</option>
          <option value="completed">Completed</option>
          <option value="rejected">Rejected</option>
        </select>
        <span style={{ fontSize: 13, color: 'var(--gray-400)' }}>{txs.length} results</span>
      </div>

      {/* Review Modal */}
      {showModal && (
        <ReviewModal
          tx={showModal}
          note={note}
          setNote={setNote}
          processing={processing}
          onClose={() => setShowModal(null)}
          onProcess={process}
        />
      )}

      {/* Transactions Table */}
      <div className="table-card">
        <div className="table-header"><h3>Transactions</h3></div>
        {loading ? <div className="loading"><div className="spinner" /></div> : (
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>User</th>
                <th>Type</th>
                <th>Amount</th>
                <th>Net</th>
                <th>Method</th>
                <th>Tx ID / Acct #</th>
                <th>Sender / Acct Name</th>
                <th>Status</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {txs.map(tx => {
                const details = parseDetails(tx.payment_details);
                const txId = tx.transaction_id || details.transaction_id;
                const senderName = tx.sender_name || details.sender_name;
                const isMoMo = tx.payment_method && tx.payment_method !== 'bank_transfer';

                return (
                  <tr key={tx.id}>
                    <td>{tx.id}</td>
                    <td>
                      <div style={{ fontWeight: 500 }}>{tx.username}</div>
                      <div style={{ fontSize: 11, color: 'var(--gray-400)' }}>{tx.email}</div>
                    </td>
                    <td>{typeIcon[tx.type] || '💱'} {tx.type.replace(/_/g, ' ')}</td>
                    <td style={{ fontWeight: 600 }}>GHS {parseFloat(tx.amount).toFixed(2)}</td>
                    <td style={{ color: 'var(--success)', fontWeight: 600 }}>GHS {parseFloat(tx.net_amount).toFixed(2)}</td>
                    <td>{tx.payment_method?.replace(/_/g, ' ') || '-'}</td>
                    <td>
                      {tx.type === 'withdrawal' ? (
                        details.account_number
                          ? <code style={{ fontSize: 11, background: '#eff6ff', padding: '2px 6px', borderRadius: 4, color: '#1e40af' }}>{details.account_number}</code>
                          : <span style={{ color: '#dc2626', fontSize: 11 }}>⚠️ None</span>
                      ) : isMoMo ? (
                        txId
                          ? <code style={{ fontSize: 11, background: '#f0fff4', padding: '2px 6px', borderRadius: 4, color: '#166534' }}>{txId}</code>
                          : <span style={{ color: '#dc2626', fontSize: 11 }}>⚠️ None</span>
                      ) : '-'}
                    </td>
                    <td>
                      {tx.type === 'withdrawal' ? (
                        details.account_name
                          ? <span style={{ fontSize: 12 }}>{details.account_name}</span>
                          : <span style={{ color: '#dc2626', fontSize: 11 }}>⚠️ None</span>
                      ) : isMoMo ? (
                        senderName
                          ? <span style={{ fontSize: 12 }}>{senderName}</span>
                          : <span style={{ color: '#dc2626', fontSize: 11 }}>⚠️ None</span>
                      ) : '-'}
                    </td>
                    <td><span className={`badge badge-${tx.status}`}>{tx.status}</span></td>
                    <td>{new Date(tx.created_at).toLocaleDateString()}</td>
                    <td>
                      <button className="btn btn-primary btn-sm"
                        onClick={() => { setShowModal(tx); setNote(''); }}>
                        {tx.status === 'pending' ? 'Review' : 'View'}
                      </button>
                    </td>
                  </tr>
                );
              })}
              {!txs.length && (
                <tr>
                  <td colSpan={11} style={{ textAlign: 'center', color: 'var(--gray-400)', padding: 30 }}>
                    No transactions found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}